import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from 'axios';
import API_BASE from './apiBase';
import Swal from 'sweetalert2';
import { maskEmail, maskPhone } from './maskUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPaperPlane,
  faCheckCircle,
  faExclamationCircle,
  faFilePdf,
  faUsers,
  faSearch,
  faEnvelope,
  faPhone,
  faBuilding,
  faMapMarkerAlt,
  faUserTie,
  faRotateRight
} from '@fortawesome/free-solid-svg-icons';
import {
  normaliseStatus,
  normaliseRole,
  consentKey,
  fetchNoticePdf,
  sendEmails
} from './NoticesPage';
import { formatCompactNumber } from './numberFormatUtils';
import '../css/App.css';

const ENTITY_OPTIONS = ["Employees", "Vendors & Suppliers", "Partners", "Customers"];

const normalizeRecipientValue = (value) => String(value || '').trim().toLowerCase().replace(/[_\s-]+/g, ' ');

const normalizeEntityValue = (value) => {
  const normalized = normalizeRecipientValue(value);
  const aliases = {
    employees: 'employee',
    emp: 'employee',
    vendors: 'vendor',
    suppliers: 'vendor',
    partners: 'partner',
    'partner(s)': 'partner',
    customers: 'customer',
    clients: 'customer',
  };
  return aliases[normalized] || normalized;
};

const getRecipientMetadata = (recipient) => {
  let data = recipient?.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (error) {
      data = null;
    }
  }

  const nested = data && typeof data === 'object' ? data : {};
  const profile = nested.profile && typeof nested.profile === 'object' ? nested.profile : {};
  const user = nested.user && typeof nested.user === 'object' ? nested.user : {};
  const firstValue = (...values) => values.find(
    (value) => value !== undefined && value !== null && String(value).trim() !== ''
  );

  return {
    location: firstValue(
      recipient?.location, recipient?.locality, recipient?.city, recipient?.user_location,
      nested.location, nested.locality, nested.city, nested.user_location,
      profile.location, profile.locality, profile.city,
      user.location, user.locality, user.city
    ),
    department: firstValue(
      recipient?.department, recipient?.department_name, recipient?.dept,
      nested.department, nested.department_name, nested.dept,
      profile.department, profile.department_name,
      user.department, user.department_name
    ),
    entity: firstValue(
      recipient?.entity, recipient?.group, recipient?.group_name, recipient?.user_group, recipient?.entity_type,
      nested.entity, nested.group, nested.group_name, nested.user_group, nested.entity_type,
      profile.entity, profile.group, profile.group_name,
      user.entity, user.group, user.group_name
    ),
  };
};

const getRecipientEntity = (recipient, metadata) => {
  const groupTerms = {
    employee: ['employee', 'staff', 'member'],
    vendor: ['vendor', 'supplier'],
    partner: ['partner', 'affiliate'],
    customer: ['customer', 'client', 'buyer', 'consumer'],
  };
  const matchesGroup = (value, group) => {
    const normalized = normalizeRecipientValue(value);
    const terms = groupTerms[group] || [group];
    return terms.some(term => normalized.includes(term)) ||
      (group === 'employee' && /(^| )emp($| )/.test(normalized));
  };

  const explicitEntity = normalizeEntityValue(metadata.entity);
  for (const group of Object.keys(groupTerms)) {
    if (matchesGroup(explicitEntity, group)) return group;
  }

  const data = (() => {
    if (recipient?.data && typeof recipient.data === 'object') return recipient.data;
    if (typeof recipient?.data === 'string') {
      try {
        const parsed = JSON.parse(recipient.data);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        return {};
      }
    }
    return {};
  })();
  const profile = data.profile && typeof data.profile === 'object' ? data.profile : {};
  const user = data.user && typeof data.user === 'object' ? data.user : {};
  const roleText = [
    recipient?.role, recipient?.user_role, recipient?.type, recipient?.consent_type,
    recipient?.category, recipient?.template, recipient?.consent,
    data.role, data.user_role, data.type, data.consent_type, data.category,
    profile.role, profile.type, profile.consent_type,
    user.role, user.type, user.consent_type,
  ].filter(Boolean).join(' ');

  for (const group of Object.keys(groupTerms)) {
    if (matchesGroup(roleText, group)) return group;
  }
  return '';
};

/* SVG Icons */
const DeptIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" fill="currentColor" />
  </svg>
);

const LocationIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z" fill="currentColor" />
  </svg>
);

const NoticeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM11 15H9v-2h2v2zm4 0h-2v-2h2v2zm-4 4H9v-2h2v2zm4 0h-2v-2h2v2z" fill="currentColor" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s' }}>
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* Sent counter component */
function SentCounterPill({ count }) {
  const prevRef = useRef(count);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (count !== prevRef.current) {
      prevRef.current = count;
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 600);
      return () => clearTimeout(t);
    }
  }, [count]);

  return (
    <div className={`np-sent-pill${flash ? ' np-sent-pill--flash' : ''}`} style={{ marginLeft: 'auto' }}>
      <span className="np-sent-dot" aria-hidden="true" />
      <span className="np-sent-label">Notices Sent</span>
      <span className="np-sent-count" aria-live="polite" title={Number(count || 0).toLocaleString()}>
        {formatCompactNumber(count)}
      </span>
    </div>
  );
}

export default function SmartNotices({ user, onBack, onMenu }) {
  const [location, setLocation] = useState("");
  const [department, setDepartment] = useState("");
  const [entity, setEntity] = useState("");
  const [selectedNoticeId, setSelectedNoticeId] = useState("");

  const [locationOptions, setLocationOptions] = useState([]);
  const [fetchedDepartments, setFetchedDepartments] = useState([]);
  const [privacyNotices, setPrivacyNotices] = useState([]);

  const [allConsents, setAllConsents] = useState([]);
  const [recipientRows, setRecipientRows] = useState([]);
  const [recipientCount, setRecipientCount] = useState(0);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientLoadError, setRecipientLoadError] = useState('');
  const [selectedRecipientKeys, setSelectedRecipientKeys] = useState(new Set());
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [modalSearch, setModalSearch] = useState('');

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [notification, setNotification] = useState(null);

  /* Shared Sent Total counter */
  const tenantId = Number(user?.tenant_id || user?.tenantId || localStorage.getItem('tenant_id') || 0);
  const _sentStorageKey = `np_sent_total_${tenantId}`;
  const [sentTotal, setSentTotal] = useState(0);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(_sentStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.count) setSentTotal(Number(parsed.count) || 0);
      }
    } catch (_) { }
  }, [_sentStorageKey]);

  const incrementSentTotal = useCallback((delta) => {
    if (!delta) return;
    setSentTotal(prev => {
      const next = prev + delta;
      try {
        localStorage.setItem(_sentStorageKey, JSON.stringify({ count: next, ts: Date.now() }));
      } catch (_) { }
      return next;
    });
  }, [_sentStorageKey]);

  const recipientKey = (recipient, index = 0) => String(
    recipient?.id || recipient?.public_id || recipient?.user_id || recipient?.email || `recipient-${index}`
  );

  const departmentOptions = useMemo(() => {
    if (!fetchedDepartments || fetchedDepartments.length === 0) return [];
    return fetchedDepartments.map(d => (d.name || d.department || '').toString()).filter(Boolean);
  }, [fetchedDepartments]);

  const isStep1Done = Boolean(location);
  const isStep2Done = Boolean(department);
  const isStep3Done = Boolean(entity);
  const isStep4Done = Boolean(selectedNoticeId);

  const selectedNotice = useMemo(() => {
    if (!selectedNoticeId) return null;
    return privacyNotices.find(n => String(n.id || n.public_id) === String(selectedNoticeId)) || null;
  }, [privacyNotices, selectedNoticeId]);

  const selectedRecipientCount = useMemo(() => {
    return recipientRows.filter((r, idx) => selectedRecipientKeys.has(recipientKey(r, idx))).length;
  }, [recipientRows, selectedRecipientKeys]);

  const canSubmit = isStep1Done && isStep2Done && isStep3Done && isStep4Done && !loadingRecipients && selectedRecipientCount > 0 && !sending;

  // Reset downstream when upstream selection changes
  useEffect(() => {
    setDepartment("");
    setEntity("");
  }, [location]);

  useEffect(() => {
    setEntity("");
  }, [department]);

  // Load configured locations, departments, and privacy notices on mount
  useEffect(() => {
    let mounted = true;

    // 1. Locations
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/manager/consent-options`);
        if (!mounted) return;
        const configuredLocations = res.data?.location || [];
        const locs = configuredLocations.map(option => {
          if (typeof option === 'string') return option;
          return option.value || option.key || option.label;
        }).filter(Boolean);
        setLocationOptions(locs);
      } catch (e) {
        if (mounted) setLocationOptions([]);
      }
    })();

    // 2. Departments
    (async () => {
      try {
        const url = tenantId ? `${API_BASE}/manager/departments?tenant_id=${encodeURIComponent(tenantId)}` : `${API_BASE}/manager/departments`;
        const res = await axios.get(url);
        if (!mounted) return;
        const deps = res.data && res.data.departments ? res.data.departments : [];
        setFetchedDepartments(Array.isArray(deps) ? deps : []);
      } catch (e) {
        if (mounted) setFetchedDepartments([]);
      }
    })();

    // 3. Privacy Notices
    (async () => {
      try {
        const params = tenantId ? { tenant_id: tenantId } : {};
        const res = await axios.get(`${API_BASE}/user/privacy-notices`, { params });
        if (!mounted) return;
        const list = res.data?.privacy_notices || [];
        setPrivacyNotices(Array.isArray(list) ? list : []);
      } catch (e) {
        if (mounted) setPrivacyNotices([]);
      }
    })();

    return () => { mounted = false; };
  }, [tenantId]);

  // Load consents for the tenant when Location, Department, and Entity are selected
  useEffect(() => {
    if (!(location && department && entity)) {
      setAllConsents([]);
      setRecipientRows([]);
      setRecipientCount(0);
      setSelectedRecipientKeys(new Set());
      setRecipientLoadError('');
      setLoadingRecipients(false);
      return undefined;
    }

    if (!tenantId) {
      setAllConsents([]);
      setRecipientRows([]);
      setRecipientCount(0);
      setSelectedRecipientKeys(new Set());
      setRecipientLoadError('Unable to identify tenant.');
      setLoadingRecipients(false);
      return undefined;
    }

    const controller = new AbortController();
    const loadConsents = async () => {
      setLoadingRecipients(true);
      setAllConsents([]);
      setRecipientRows([]);
      setRecipientCount(0);
      setSelectedRecipientKeys(new Set());
      setRecipientLoadError('');

      try {
        const smartUrl = `${API_BASE}/user/consent/smart-recipients`;
        const params = {
          tenant_id: tenantId,
          location,
          department,
          entity,
          status: 'accepted',
          _refresh: Date.now(),
        };
        const requestConfig = {
          params,
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
          signal: controller.signal,
        };

        let response;
        try {
          response = await axios.get(smartUrl, requestConfig);
        } catch (smartErr) {
          if (controller.signal.aborted) return;
          console.warn('[SmartNotices] smart-recipients fallback to /user/consent', smartErr);
          const legacyUrl = `${API_BASE}/user/consent`;
          response = await axios.get(legacyUrl, {
            ...requestConfig,
            params: {
              tenant_id: tenantId,
              location,
              department,
              role: entity,
              status: 'accepted',
              page: 1,
              page_size: 2000,
              _refresh: Date.now(),
            }
          });
        }

        if (controller.signal.aborted) return;

        const data = response?.data;
        const recipients = data?.recipients || data?.consents || (Array.isArray(data) ? data : []);
        const allRecords = data?.all_records || (Array.isArray(data?.consents) ? data.consents : recipients);

        const recList = Array.isArray(recipients) ? recipients : [];
        const allList = Array.isArray(allRecords) ? allRecords : recList;

        setAllConsents(allList);

        if (data?.recipients && Array.isArray(data.recipients)) {
          setRecipientRows(recList);
          setRecipientCount(Number(data.total) || recList.length);
          setSelectedRecipientKeys(new Set(recList.map((r, idx) => recipientKey(r, idx))));
        } else {
          // Fallback client filter
          const loc = normalizeRecipientValue(location);
          const dept = normalizeRecipientValue(department);
          const ent = normalizeEntityValue(entity);

          const seenIdentities = new Set();
          const filtered = [];

          for (const r of allList) {
            try {
              const st = normaliseStatus(r);
              if (st !== 'accepted') continue;

              const metadata = getRecipientMetadata(r);
              const rl = normalizeRecipientValue(metadata.location || metadata.locality);
              const rd = normalizeRecipientValue(metadata.department);
              const re = getRecipientEntity(r, metadata);

              const matchLocality = rl === loc;
              const matchDept = rd === dept;
              const matchEntity = re === ent || (!re && normalizeEntityValue(metadata.entity) === ent);

              if (!matchLocality || !matchDept || !matchEntity) continue;

              const email = (r.email || '').toString().trim().toLowerCase();
              const uid = (r.user_id || r.userId || r.id || '').toString();
              const identityKey = email || uid || consentKey(r);
              if (seenIdentities.has(identityKey)) continue;
              seenIdentities.add(identityKey);

              filtered.push(r);
            } catch (_) {
              continue;
            }
          }

          setRecipientRows(filtered);
          setRecipientCount(filtered.length);
          setSelectedRecipientKeys(new Set(filtered.map((r, idx) => recipientKey(r, idx))));
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setAllConsents([]);
          setRecipientRows([]);
          setRecipientCount(0);
          setSelectedRecipientKeys(new Set());
          setRecipientLoadError(err?.response?.data?.detail || 'Unable to load recipients. Please try again.');
        }
      } finally {
        if (!controller.signal.aborted) setLoadingRecipients(false);
      }
    };

    loadConsents();
    return () => controller.abort();
  }, [location, department, entity, tenantId]);

  /* ═══════════════════════════════════════════════════════════════
     SEND / SUBMISSION HANDLER
  ═══════════════════════════════════════════════════════════════ */
  const handleSubmit = async () => {
    if (!canSubmit || !selectedNotice) return;
    const selectedRows = recipientRows.filter((r, idx) => selectedRecipientKeys.has(recipientKey(r, idx)));
    if (selectedRows.length === 0) return;

    const conf = await Swal.fire({
      title: 'Send Smart Notice',
      text: `Send "${selectedNotice.title || selectedNotice.consent_type}" to ${selectedRows.length} accepted recipient${selectedRows.length !== 1 ? 's' : ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Send Notice',
      cancelButtonText: 'Cancel',
      customClass: { popup: 'swal-popup-custom', confirmButton: 'swal-confirm-btn', cancelButton: 'swal-cancel-btn' },
      buttonsStyling: false
    });
    if (!conf.isConfirmed) return;

    setSending(true);
    try {
      const result = await sendEmails(selectedRows, { notice: selectedNotice });
      incrementSentTotal(result.success);
      setSending(false);
      setSubmitted(true);
      setNotification({
        success: result.success,
        failed: result.fail,
        errors: result.errors,
        location,
        department,
        entity,
        noticeTitle: selectedNotice.title || selectedNotice.consent_type,
      });

      if (result.success > 0) {
        try { window.toastSuccess(`Sent: ${result.success} · Failed: ${result.fail}`); } catch (_) { }
      } else {
        try { window.toastError(`Failed to send notices. Please try again.`); } catch (_) { }
      }
    } catch (err) {
      setSending(false);
      Swal.fire({
        icon: 'error',
        title: 'Send Failed',
        text: err?.message || 'Failed to dispatch notices.',
        customClass: { popup: 'swal-popup-custom' }
      });
    }
  };

  const handleReset = () => {
    setLocation("");
    setDepartment("");
    setEntity("");
    setSelectedNoticeId("");
    setSubmitted(false);
    setNotification(null);
    setActiveDropdown(null);
    setRecipientModalOpen(false);
    setSelectedRecipientKeys(new Set());
  };

  /* Step Badge Component */
  const StepBadge = ({ n, done }) => (
    <div style={{
      width: "24px", height: "24px", borderRadius: "50%", flexShrink: 0,
      background: done ? "#10b981" : "rgba(16, 185, 129, 0.12)",
      border: `1.5px solid ${done ? "#10b981" : "rgba(16, 185, 129, 0.35)"}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: done ? "#fff" : "#059669", fontSize: "0.72rem", fontWeight: 700,
      transition: "all 0.3s",
    }}>
      {done ? <CheckIcon /> : n}
    </div>
  );

  /* Dropdown Selector Component */
  const SelectBox = ({ id, label, value, options, onChange, disabled, icon, placeholder }) => {
    const isOpen = activeDropdown === id;
    const [filterText, setFilterText] = useState('');

    useEffect(() => {
      if (!isOpen) setFilterText('');
    }, [isOpen]);

    const visible = (options || []).filter(opt => {
      const text = typeof opt === 'object' ? (opt.label || opt.title || '') : String(opt);
      return text.toLowerCase().includes(filterText.toLowerCase());
    });

    const displayLabel = useMemo(() => {
      if (!value) return placeholder;
      if (typeof options[0] === 'object') {
        const found = options.find(o => String(o.id || o.value) === String(value));
        return found ? (found.label || found.title || found.name) : value;
      }
      return value;
    }, [value, options, placeholder]);

    return (
      <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
        <label style={{
          display: "flex", alignItems: "center", gap: "6px",
          fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.08em",
          textTransform: "uppercase", color: disabled ? "#94a3b8" : "#0f766e",
          marginBottom: "6px", fontFamily: "'DM Sans', sans-serif",
        }}>
          <span style={{ opacity: disabled ? 0.4 : 1 }}>{icon}</span> {label}
        </label>

        <div
          onClick={(e) => { e.stopPropagation(); if (!disabled) setActiveDropdown(isOpen ? null : id); }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "0.75rem 0.9rem",
            background: disabled ? "#f8fafc" : "#ffffff",
            border: `1.5px solid ${value ? "#059669" : isOpen ? "#34d399" : "#e2e8f0"}`,
            borderRadius: "10px", cursor: disabled ? "not-allowed" : "pointer",
            color: disabled ? "#94a3b8" : value ? "#0f172a" : "#64748b",
            fontFamily: "'DM Sans', sans-serif", fontSize: "0.86rem",
            boxShadow: isOpen ? "0 0 0 4px rgba(16, 185, 129, 0.12)" : "none",
            transition: "all 0.2s", whiteSpace: "nowrap", overflow: "hidden",
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", flex: 1, fontWeight: value ? 600 : 400 }}>
            {displayLabel}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8, flexShrink: 0 }}>
            <span style={{ color: disabled ? "#cbd5e1" : "#059669" }}>
              <ChevronIcon open={isOpen} />
            </span>
          </div>
        </div>

        {isOpen && (
          <div className="sc-dropdown" style={{
            position: "absolute", top: "calc(100% + 5px)", left: 0, right: 0,
            background: "#ffffff", border: "1.5px solid rgba(16, 185, 129, 0.25)",
            borderRadius: "10px", zIndex: 300, maxHeight: "250px", overflowY: "auto",
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
          }}>
            <div style={{ padding: '0.5rem', borderBottom: '1px solid #f1f5f9' }} onClick={(e) => e.stopPropagation()}>
              <input
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Search..."
                style={{ width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.84rem' }}
                autoFocus
              />
            </div>
            <div>
              {visible.length === 0 ? (
                <div style={{ padding: '0.75rem', fontSize: '0.82rem', color: '#94a3b8', textAlign: 'center' }}>No options found</div>
              ) : (
                visible.map((opt) => {
                  const optVal = typeof opt === 'object' ? (opt.id || opt.value) : opt;
                  const optLabel = typeof opt === 'object' ? (opt.label || opt.title || opt.name) : opt;
                  const optSub = typeof opt === 'object' ? (opt.sub || opt.consent_type) : null;
                  const isSelected = String(value) === String(optVal);

                  return (
                    <div
                      key={String(optVal)}
                      onClick={(e) => { e.stopPropagation(); onChange(String(optVal)); setActiveDropdown(null); }}
                      style={{
                        padding: "0.65rem 0.85rem", cursor: "pointer",
                        fontSize: "0.84rem",
                        color: isSelected ? "#059669" : "#0f172a",
                        background: isSelected ? "rgba(16, 185, 129, 0.08)" : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        fontFamily: "'DM Sans', sans-serif", transition: "background 0.15s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = isSelected ? "rgba(16, 185, 129, 0.12)" : "rgba(16, 185, 129, 0.04)"}
                      onMouseLeave={e => e.currentTarget.style.background = isSelected ? "rgba(16, 185, 129, 0.08)" : "transparent"}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: isSelected ? 600 : 400 }}>{optLabel}</span>
                        {optSub && <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{optSub}</span>}
                      </div>
                      {isSelected && <span style={{ color: '#059669' }}><CheckIcon /></span>}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  /* Modal Filtered Recipients */
  const modalFilteredRecipients = useMemo(() => {
    if (!modalSearch.trim()) return recipientRows;
    const q = modalSearch.toLowerCase().trim();
    return recipientRows.filter(r => {
      const name = (r.username || r.full_name || r.name || '').toLowerCase();
      const email = (r.email || '').toLowerCase();
      const phone = (r.phone || '').toLowerCase();
      return name.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [recipientRows, modalSearch]);

  /* ── Success Screen ── */
  if (submitted && notification) {
    return (
      <div
        onClick={() => setActiveDropdown(null)}
        style={{
          minHeight: "85vh", background: "#f8fafc",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "2rem", fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: "560px", width: "100%", background: "#fff", borderRadius: 20, padding: "2.5rem", border: "1px solid #e2e8f0", boxShadow: "0 20px 45px rgba(0,0,0,0.06)", textAlign: "center" }}>
          <div style={{
            width: "72px", height: "72px", borderRadius: "50%",
            background: "linear-gradient(135deg, #10b981, #059669)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 1.25rem", color: "#fff", fontSize: 32,
            boxShadow: "0 10px 25px rgba(16, 185, 129, 0.3)"
          }}>
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>

          <h2 style={{ fontSize: "1.75rem", fontWeight: 700, color: "#0f172a", margin: "0 0 0.5rem" }}>
            Notices Sent Successfully!
          </h2>
          <p style={{ color: "#64748b", fontSize: "0.95rem", margin: "0 0 1.75rem" }}>
            Dispatched privacy notice to accepted recipients.
          </p>

          <div style={{
            background: "#f0fdf4", border: "1px solid #bbf7d0",
            borderRadius: "14px", padding: "1.25rem", textAlign: "left", marginBottom: "1.5rem",
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: '0.86rem' }}>
              <span style={{ fontWeight: 600, color: '#166534' }}>Notice:</span>
              <span style={{ color: '#0f172a', fontWeight: 600 }}>{notification.noticeTitle}</span>

              <span style={{ fontWeight: 600, color: '#166534' }}>Location:</span>
              <span style={{ color: '#334155' }}>{notification.location}</span>

              <span style={{ fontWeight: 600, color: '#166534' }}>Department:</span>
              <span style={{ color: '#334155' }}>{notification.department}</span>

              <span style={{ fontWeight: 600, color: '#166534' }}>Group:</span>
              <span style={{ color: '#334155' }}>{notification.entity}</span>

              <span style={{ fontWeight: 600, color: '#166534' }}>Sent:</span>
              <span style={{ color: '#15803d', fontWeight: 700 }}>{notification.success} recipients</span>

              {notification.failed > 0 && (
                <>
                  <span style={{ fontWeight: 600, color: '#b91c1c' }}>Failed:</span>
                  <span style={{ color: '#b91c1c', fontWeight: 700 }}>{notification.failed}</span>
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              type="button"
              className="np-btn np-btn--ghost"
              onClick={() => {
                if (onBack) onBack();
                else if (onMenu) onMenu('notices');
                try { window.history.pushState({}, '', '/user/notices'); } catch (_) { }
              }}
              style={{ padding: '10px 20px', fontSize: 14 }}
            >
              ← Back to Notices
            </button>
            <button
              type="button"
              className="np-btn np-btn--primary"
              onClick={handleReset}
              style={{ padding: '10px 22px', fontSize: 14, background: '#10b981', borderColor: '#10b981' }}
            >
              Send Another Notice
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => setActiveDropdown(null)}
      style={{
        minHeight: "100vh", background: "#f8fafc",
        padding: "1.5rem", fontFamily: "'DM Sans', sans-serif"
      }}
    >
      {/* ── Top Header Bar ── */}
      <div style={{
        background: "#ffffff", border: "1px solid #e2e8f0",
        borderRadius: 16, padding: "0.85rem 1.5rem", marginBottom: "1.5rem",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        boxShadow: "0 2px 10px rgba(0,0,0,0.03)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button
            type="button"
            className="np-btn np-btn--ghost"
            onClick={() => {
              if (onBack) onBack();
              else if (onMenu) onMenu('notices');
              try { window.history.pushState({}, '', '/user/notices'); } catch (_) { }
            }}
            style={{ padding: "6px 12px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 12 }} />
            Back to Notices
          </button>
          <div style={{ width: 1, height: 22, background: "#e2e8f0" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8, background: "#f0fdf4",
              border: "1px solid #bbf7d0", color: "#059669", display: "flex",
              alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14
            }}>
              N+
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
                Smart Notices
                <span style={{ fontSize: 11, background: "#ecfdf5", color: "#059669", padding: "2px 8px", borderRadius: 99, border: "1px solid #a7f3d0" }}>
                  Active Filter
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                Send privacy notices to accepted recipients filtered by location, department & group
              </div>
            </div>
          </div>
        </div>

        <SentCounterPill count={sentTotal} />
      </div>

      {/* ── Progress bar ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1.5rem", gap: "0.5rem" }}>
        {[
          { n: 1, label: "Location", done: isStep1Done },
          { n: 2, label: "Department", done: isStep2Done },
          { n: 3, label: "Group", done: isStep3Done },
          { n: 4, label: "Notice", done: isStep4Done },
        ].map((s, i) => (
          <div key={s.n} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <StepBadge n={s.n} done={s.done} />
              <span style={{
                fontSize: "0.65rem", color: s.done || i === 0 ? "#059669" : "#94a3b8",
                marginTop: "4px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase",
              }}>
                {s.label}
              </span>
            </div>
            {i < 3 && (
              <div style={{
                width: "55px", height: "2px", marginBottom: "14px",
                background: s.done ? "#10b981" : "#e2e8f0",
                transition: "background 0.3s", borderRadius: "1px",
              }} />
            )}
          </div>
        ))}
      </div>

      {/* ── Main Form Card ── */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#ffffff", border: "1px solid #e2e8f0",
          borderRadius: 20, padding: "1.75rem",
          boxShadow: "0 4px 20px rgba(0,0,0,0.04)", maxWidth: 1200, margin: "0 auto"
        }}
      >
        {/* Horizontal row of 4 selectors */}
        <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap" }}>
          {/* Step 1 – Location */}
          <SelectBox
            id="location"
            label="Location"
            value={location}
            options={locationOptions.slice().sort((a, b) => String(a).localeCompare(String(b)))}
            onChange={setLocation}
            icon={<LocationIcon />}
            placeholder="Choose location / city…"
          />

          <div style={{ width: "1px", background: "#f1f5f9", alignSelf: "stretch", marginTop: "24px" }} />

          {/* Step 2 – Department */}
          <SelectBox
            id="department"
            label="Department"
            value={department}
            options={departmentOptions.slice().sort((a, b) => String(a).localeCompare(String(b)))}
            onChange={setDepartment}
            disabled={!isStep1Done}
            icon={<DeptIcon />}
            placeholder={isStep1Done ? "Choose department…" : "Select location first"}
          />

          <div style={{ width: "1px", background: "#f1f5f9", alignSelf: "stretch", marginTop: "24px" }} />

          {/* Step 3 – Group */}
          <SelectBox
            id="entity"
            label="Group"
            value={entity}
            options={ENTITY_OPTIONS}
            onChange={setEntity}
            disabled={!isStep2Done}
            icon={<FontAwesomeIcon icon={faUsers} style={{ fontSize: 13 }} />}
            placeholder={isStep2Done ? "Choose group…" : "Select department first"}
          />

          <div style={{ width: "1px", background: "#f1f5f9", alignSelf: "stretch", marginTop: "24px" }} />

          {/* Step 4 – Privacy Notice */}
          <SelectBox
            id="notice"
            label="Privacy Notice"
            value={selectedNoticeId}
            options={privacyNotices.map(n => ({
              id: String(n.id || n.public_id),
              title: n.title || n.consent_type || 'Untitled Notice',
              sub: n.consent_type ? `Type: ${n.consent_type}` : ''
            }))}
            onChange={setSelectedNoticeId}
            disabled={!isStep3Done}
            icon={<NoticeIcon />}
            placeholder={isStep3Done ? "Choose privacy notice…" : "Select group first"}
          />
        </div>

        {/* ── Recipients Preview Card: ONLY ACCEPTED RECIPIENTS ── */}
        {(location && department && entity) && (
          <div style={{
            background: "#f0fdf4", border: "1px solid #bbf7d0",
            borderRadius: "14px", padding: "1.25rem", marginTop: "1.5rem",
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: "0.72rem", color: "#166534", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>
                    Matching Recipients
                  </span>
                  <span className="np-badge np-badge--accepted" style={{ fontSize: 11, padding: "2px 8px" }}>
                    Only Accepted Records
                  </span>
                </div>
                <p aria-live="polite" style={{ color: "#334155", fontSize: "0.92rem", margin: "6px 0 0", fontWeight: 500 }}>
                  {loadingRecipients ? (
                    <span>Loading accepted recipients…</span>
                  ) : (
                    <span>
                      Found <strong style={{ color: "#0f172a" }}>{recipientCount}</strong> accepted recipient{recipientCount !== 1 ? 's' : ''} in {department} ({location} · {entity})
                    </span>
                  )}
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  type="button"
                  className="np-btn np-btn--ghost"
                  onClick={() => setRecipientModalOpen(true)}
                  disabled={loadingRecipients || recipientCount === 0}
                  style={{
                    background: '#fff', borderColor: '#86efac', color: '#166534',
                    padding: '8px 16px', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8
                  }}
                >
                  <FontAwesomeIcon icon={faUsers} />
                  View & Select Recipients ({selectedRecipientCount} of {recipientCount})
                </button>
              </div>
            </div>

            {recipientLoadError && (
              <p role="status" style={{ color: "#b91c1c", fontSize: "0.85rem", marginTop: "0.75rem", margin: "0" }}>
                {recipientLoadError}
              </p>
            )}
          </div>
        )}

        {/* ── Selected Notice Preview ── */}
        {selectedNotice && (
          <div style={{
            background: "#f8fafc", border: "1px solid #e2e8f0",
            borderRadius: "14px", padding: "1.25rem", marginTop: "1.25rem",
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#059669', background: '#dcfce7', padding: '2px 8px', borderRadius: 99 }}>
                    Selected Notice
                  </span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    Type: <strong>{selectedNotice.consent_type || 'Notice'}</strong>
                  </span>
                </div>
                <h4 style={{ margin: "4px 0", fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                  {selectedNotice.title || selectedNotice.consent_type}
                </h4>
                {selectedNotice.description && (
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "#475569" }}>
                    {selectedNotice.description}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#eff6ff', color: '#2563eb', padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>
                <FontAwesomeIcon icon={faFilePdf} />
                PDF Attached
              </div>
            </div>
          </div>
        )}

        {/* ── Bottom Submit Section ── */}
        <div style={{
          marginTop: "1.75rem", paddingTop: "1.25rem", borderTop: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12
        }}>
          <button
            type="button"
            className="np-btn np-btn--ghost"
            onClick={handleReset}
            style={{ padding: "8px 18px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: 12 }} />
            Reset Filters
          </button>

          <button
            type="button"
            className="np-btn np-btn--primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              padding: "10px 24px", fontSize: 14, fontWeight: 600,
              background: canSubmit ? "linear-gradient(135deg, #10b981, #059669)" : "#94a3b8",
              borderColor: canSubmit ? "#059669" : "#94a3b8",
              cursor: canSubmit ? "pointer" : "not-allowed",
              display: "inline-flex", alignItems: "center", gap: 8,
              boxShadow: canSubmit ? "0 4px 14px rgba(16, 185, 129, 0.3)" : "none"
            }}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
            {sending ? 'Sending Notices…' : `Send Notice to ${selectedRecipientCount} Recipient${selectedRecipientCount !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>

      {/* ── Matching Recipients Modal ── */}
      {recipientModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Matching Accepted Recipients"
          onClick={(event) => { if (event.target === event.currentTarget) setRecipientModalOpen(false); }}
          style={{
            position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,23,42,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
          }}
        >
          <div style={{
            width: "min(1050px, 100%)", maxHeight: "88vh", overflow: "hidden", background: "#fff",
            borderRadius: 18, boxShadow: "0 24px 70px rgba(15,23,42,0.25)", display: "flex", flexDirection: "column",
          }}>
            {/* Modal Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1.25rem 1.5rem", borderBottom: "1px solid #e2e8f0" }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: "#0f172a", fontSize: "1.1rem", fontWeight: 700 }}>
                    Matching Accepted Recipients
                  </span>
                  <span className="np-badge np-badge--accepted" style={{ fontSize: 11 }}>
                    Accepted Only
                  </span>
                </div>
                <div style={{ color: "#64748b", fontSize: "0.84rem", marginTop: 4 }}>
                  Showing recipients with accepted consent for <strong>{entity}</strong> in <strong>{department}</strong> ({location}).
                </div>
              </div>
              <button
                type="button"
                onClick={() => setRecipientModalOpen(false)}
                aria-label="Close"
                style={{ border: 0, background: "transparent", color: "#64748b", fontSize: 26, cursor: "pointer", lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            {/* Modal Search Bar & Selection Summary */}
            <div style={{ padding: "0.85rem 1.5rem", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
              <div style={{ position: "relative", flex: 1, maxWidth: 360 }}>
                <FontAwesomeIcon icon={faSearch} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 12 }} />
                <input
                  type="text"
                  placeholder="Filter recipients by name, email, phone…"
                  value={modalSearch}
                  onChange={(e) => setModalSearch(e.target.value)}
                  style={{ width: "100%", padding: "7px 12px 7px 32px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: "0.84rem" }}
                />
              </div>
              <div style={{ fontSize: "0.84rem", color: "#475569" }}>
                <strong>{selectedRecipientCount}</strong> of <strong>{recipientRows.length}</strong> selected
              </div>
            </div>

            {/* Modal Table Content */}
            <div style={{ overflow: "auto", padding: "0 1.5rem 1.5rem", maxHeight: "55vh" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #e2e8f0", color: "#475569", fontSize: "0.76rem", textAlign: "left", position: "sticky", top: 0, background: "#fff", zIndex: 10 }}>
                    <th style={{ padding: "0.85rem 0.5rem", width: 44 }}>
                      <input
                        type="checkbox"
                        aria-label="Select all recipients"
                        checked={recipientRows.length > 0 && selectedRecipientCount === recipientRows.length}
                        onChange={(event) => setSelectedRecipientKeys(event.target.checked
                          ? new Set(recipientRows.map((r, idx) => recipientKey(r, idx)))
                          : new Set())}
                      />
                    </th>
                    <th style={{ padding: "0.85rem 0.5rem" }}>User</th>
                    <th style={{ padding: "0.85rem 0.5rem" }}>Email</th>
                    <th style={{ padding: "0.85rem 0.5rem" }}>Phone</th>
                    <th style={{ padding: "0.85rem 0.5rem" }}>Location</th>
                    <th style={{ padding: "0.85rem 0.5rem" }}>Department</th>
                    <th style={{ padding: "0.85rem 0.5rem" }}>Group</th>
                    <th style={{ padding: "0.85rem 0.5rem" }}>Status</th>
                    <th style={{ padding: "0.85rem 0.5rem" }}>Consent Record</th>
                  </tr>
                </thead>
                <tbody>
                  {modalFilteredRecipients.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "2rem", color: "#94a3b8", fontSize: "0.9rem" }}>
                        No accepted recipients match the search query.
                      </td>
                    </tr>
                  ) : (
                    modalFilteredRecipients.map((recipient, index) => {
                      const key = recipientKey(recipient, index);
                      const name = recipient.username || recipient.full_name || recipient.name || recipient.Name || "Unnamed user";
                      const ct = recipient.type || recipient.consent_type || recipient.template || "Consent";
                      const isSelected = selectedRecipientKeys.has(key);

                      return (
                        <tr
                          key={key}
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            background: isSelected ? "rgba(16, 185, 129, 0.03)" : "transparent",
                            fontSize: "0.82rem"
                          }}
                        >
                          <td style={{ padding: "0.8rem 0.5rem" }}>
                            <input
                              type="checkbox"
                              aria-label={`Select ${name}`}
                              checked={isSelected}
                              onChange={() => setSelectedRecipientKeys((prev) => {
                                const next = new Set(prev);
                                if (next.has(key)) next.delete(key); else next.add(key);
                                return next;
                              })}
                            />
                          </td>
                          <td style={{ padding: "0.8rem 0.5rem", fontWeight: 600, color: "#0f172a" }}>
                            {name}
                          </td>
                          <td style={{ padding: "0.8rem 0.5rem", color: "#334155" }}>
                            {maskEmail(recipient.email) || "—"}
                          </td>
                          <td style={{ padding: "0.8rem 0.5rem", color: "#64748b" }}>
                            {maskPhone(recipient.phone) || "—"}
                          </td>
                          <td style={{ padding: "0.8rem 0.5rem", color: "#475569" }}>
                            {recipient.location || recipient.locality || location || "—"}
                          </td>
                          <td style={{ padding: "0.8rem 0.5rem", color: "#475569" }}>
                            {recipient.department || department || "—"}
                          </td>
                          <td style={{ padding: "0.8rem 0.5rem", color: "#475569" }}>
                            {entity || recipient.entity || "—"}
                          </td>
                          <td style={{ padding: "0.8rem 0.5rem" }}>
                            <span className="np-badge np-badge--accepted" style={{ fontSize: 10, padding: "2px 7px" }}>
                              Accepted
                            </span>
                          </td>
                          <td style={{ padding: "0.8rem 0.5rem", color: "#64748b" }}>
                            {ct}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Modal Footer */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.5rem", borderTop: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <span style={{ fontSize: "0.82rem", color: "#64748b" }}>
                Selected: <strong>{selectedRecipientCount}</strong> of <strong>{recipientRows.length}</strong>
              </span>
              <button
                type="button"
                className="np-btn np-btn--primary"
                onClick={() => setRecipientModalOpen(false)}
                style={{ padding: "8px 20px", fontSize: 13, background: "#10b981", borderColor: "#10b981" }}
              >
                Apply Selection ({selectedRecipientCount})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
